import { AnalyticsService } from '../types/analytics';

class AnalyticsOptimizer {
  private static instance: AnalyticsOptimizer;
  private loadTimes: Map<string, number> = new Map();
  private errorCounts: Map<string, number> = new Map();

  private constructor() {
    this.initializeOptimizer();
  }

  public static getInstance(): AnalyticsOptimizer {
    if (!AnalyticsOptimizer.instance) {
      AnalyticsOptimizer.instance = new AnalyticsOptimizer();
    }
    return AnalyticsOptimizer.instance;
  }

  private initializeOptimizer(): void {
    // Monitorear performance
    this.setupPerformanceObserver();

    // Limpiar contadores de errores periódicamente
    setInterval(() => this.resetErrorCounts(), 24 * 60 * 60 * 1000); // 24 horas
  }

  private setupPerformanceObserver(): void {
    try {
      const observer = new PerformanceObserver(list => {
        list.getEntries().forEach(entry => {
          if (entry.entryType === 'resource') {
            this.handleResourceTiming(entry);
          }
        });
      });

      observer.observe({ entryTypes: ['resource'] });
    } catch (error) {
      console.error('Error setting up PerformanceObserver:', error);
    }
  }

  private handleResourceTiming(entry: PerformanceEntry): void {
    const url = (entry as PerformanceResourceTiming).name;

    // Identificar servicios de analytics por URL
    if (url.includes('googletagmanager.com')) {
      this.loadTimes.set('GTM', entry.duration);
    } else if (url.includes('facebook.com')) {
      this.loadTimes.set('FB_PIXEL', entry.duration);
    } else if (url.includes('linkedin.com')) {
      this.loadTimes.set('LINKEDIN', entry.duration);
    } else if (url.includes('clarity.ms')) {
      this.loadTimes.set('CLARITY', entry.duration);
    }
  }

  public trackError(service: string): void {
    const currentCount = this.errorCounts.get(service) || 0;
    this.errorCounts.set(service, currentCount + 1);

    // Si hay muchos errores, implementar backoff
    if (currentCount > 5) {
      this.implementBackoff(service);
    }
  }

  private implementBackoff(service: string): void {
    // Implementar lógica de backoff exponencial
    const backoffTime = Math.min(1000 * Math.pow(2, this.errorCounts.get(service) || 1), 60000);

    console.warn(`Implementing backoff for ${service}, delay: ${backoffTime}ms`);

    // Aquí podrías implementar lógica específica de backoff para cada servicio
  }

  private resetErrorCounts(): void {
    this.errorCounts.clear();
  }

  public getLoadTimes(): Map<string, number> {
    return new Map(this.loadTimes);
  }

  public getErrorCounts(): Map<string, number> {
    return new Map(this.errorCounts);
  }

  public optimizeLoadOrder(services: AnalyticsService[]): AnalyticsService[] {
    // Ordenar servicios basado en tiempos de carga históricos
    return [...services].sort((a, b) => {
      const aTime = this.loadTimes.get(a.name) || 0;
      const bTime = this.loadTimes.get(b.name) || 0;
      return aTime - bTime;
    });
  }

  public shouldRetryService(service: string): boolean {
    const errorCount = this.errorCounts.get(service) || 0;
    return errorCount < 5; // Límite de reintentos
  }
}

export const analyticsOptimizer = AnalyticsOptimizer.getInstance();
